import { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { api, formatApiError } from '@/lib/api';
import { generateLandIntel } from '@/lib/landIntel';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Printer, RefreshCw, Brain, Sparkles, MapPin } from 'lucide-react';
import { toast } from 'sonner';
import MarkdownMessage from '@/components/MarkdownMessage';

/**
 * Pagina report completo AgriNexus Land Intelligence.
 * Layout tipo "documento": header terreno + cards KPI compatte + report Markdown a piena larghezza.
 * Stampabile (window.print).
 */
export default function LandReport() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [state, setState] = useState('loading'); // loading | ready | error
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');

  const load = async (force = false) => {
    setState('loading');
    try {
      // Prova cache — se non c'è, genera
      if (!force) {
        try {
          const r = await api.get(`/land-intel/${id}`);
          setData(r.data); setState('ready'); return;
        } catch { /* proseguo a POST */ }
      }
      const data = await generateLandIntel(id, force);
      setData(data);
      setState('ready');
      if (force) toast.success('Report rigenerato');
    } catch (e) {
      setErr(formatApiError(e)); setState('error');
    }
  };

  useEffect(() => {
    if (!user) { nav('/login'); return; }
    load(false);
    // eslint-disable-next-line
  }, [id, user]);

  if (state === 'loading') {
    return (
      <div className="max-w-4xl mx-auto px-6 py-16">
        <div className="flex items-center gap-3 mb-8">
          <div className="w-10 h-10 rounded-lg bg-foreground text-background grid place-items-center animate-pulse"><Brain size={18}/></div>
          <div>
            <div className="font-display text-2xl">Generazione report…</div>
            <div className="text-sm text-muted-foreground">Raccolgo dati climatici storici, altitudine e toponimo verificato.</div>
          </div>
        </div>
        <div className="space-y-3">
          {Array.from({length: 8}).map((_,i) => <div key={i} className="h-4 bg-secondary/50 rounded animate-pulse" style={{width: `${60 + Math.random()*40}%`}}/>)}
        </div>
      </div>
    );
  }

  if (state === 'error') {
    return (
      <div className="max-w-2xl mx-auto px-6 py-16 text-center">
        <div className="text-terracotta text-sm mb-2">Errore</div>
        <div className="font-display text-2xl">{err}</div>
        <Button variant="outline" onClick={() => load(true)} className="mt-6 rounded-full"><RefreshCw size={14} className="mr-2"/> Riprova</Button>
      </div>
    );
  }

  const { report_markdown, cards = [], compatibility = {}, geo = {}, climate, elevation_m,
          listing_title, listing_snapshot = {}, generated_at, model } = data || {};
  const loc = listing_snapshot.location || {};

  return (
    <div className="min-h-screen">
      <div className="max-w-5xl mx-auto px-6 lg:px-10 py-10 print:py-4 print:px-0">
        {/* Actions bar */}
        <div className="flex items-center justify-between mb-8 print:hidden">
          <Link to={`/terreno/${id}`} className="text-sm text-muted-foreground link-underline flex items-center gap-1">
            <ArrowLeft size={14}/> Torna all'annuncio
          </Link>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => load(true)} className="rounded-full">
              <RefreshCw size={12} className="mr-2"/> Rigenera
            </Button>
            <Button variant="outline" size="sm" onClick={() => window.print()} className="rounded-full" data-testid="landreport-print">
              <Printer size={12} className="mr-2"/> Stampa / PDF
            </Button>
          </div>
        </div>

        {/* Header */}
        <div className="border-b border-border pb-8 mb-10">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-8 h-8 rounded-lg bg-foreground text-background grid place-items-center"><Brain size={14}/></div>
            <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-terracotta">AgriNexus Land Intelligence</div>
          </div>
          <h1 className="font-display text-3xl md:text-5xl tracking-tighter">{listing_title || 'Report terreno'}</h1>
          <div className="mt-3 flex items-center gap-2 text-sm text-muted-foreground flex-wrap">
            <MapPin size={13}/>
            <span>{[loc.comune, loc.province, loc.region].filter(Boolean).join(', ')}</span>
            {elevation_m != null && <span className="opacity-60">· {Math.round(elevation_m)} m s.l.m.</span>}
            {listing_snapshot.area_hectares && <span className="opacity-60">· {listing_snapshot.area_hectares} ha</span>}
          </div>
        </div>

        {/* KPI cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-10">
          {cards.map((c, i) => (
            <div key={i} className="rounded-xl border border-border p-4 bg-card">
              <div className="text-2xl leading-none mb-2">{c.icon}</div>
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{c.label}</div>
              <div className="font-medium mt-1 text-sm leading-snug">{c.value}</div>
              {c.detail && <div className="text-[11px] text-muted-foreground mt-1.5 leading-tight line-clamp-2">{c.detail}</div>}
            </div>
          ))}
        </div>

        {/* Compatibility inline (compatta) */}
        {compatibility.has_preferences && (
          <div className="rounded-2xl border border-border bg-secondary/20 p-6 mb-10 flex items-center gap-6 flex-wrap">
            <div className="text-center shrink-0">
              <div className="font-display text-4xl leading-none">{compatibility.score}</div>
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground mt-1">Match / 100</div>
            </div>
            <div className="flex-1 grid grid-cols-2 md:grid-cols-3 gap-2 min-w-[280px]">
              {(compatibility.breakdown || []).map((b, i) => (
                <div key={i} className="text-xs flex items-center gap-1.5">
                  <span>{b.icon}</span>
                  <span className="font-medium">{b.factor}</span>
                  <span className="text-muted-foreground truncate">· {b.earned}/{b.weight}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Markdown full report */}
        <article className="max-w-none">
          <MarkdownMessage content={report_markdown}/>
        </article>

        {/* Footer */}
        <div className="mt-16 pt-8 border-t border-border text-xs text-muted-foreground space-y-1">
          {model && <div>Modello AI: {model}</div>}
          {climate && <div>Fonte clima: {climate.source} · Periodo osservazione: {climate.period}</div>}
          {geo?.display_name && <div>Toponimo OSM: {geo.display_name}</div>}
          {generated_at && <div>Report generato: {new Date(generated_at).toLocaleString('it-IT')}</div>}
          <div className="mt-3 flex items-center gap-1.5 opacity-80"><Sparkles size={11}/> Il report è un supporto informativo e non sostituisce un agronomo qualificato.</div>
        </div>
      </div>
    </div>
  );
}
